import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import type { AssignmentList, ConnectedFacility } from '../../../../../js/api';
import { AssignmentsService, SourceTypeEnum } from '../../../../../js/api';
import { Button } from '../../../components/button';
import { Dialog, DialogCloseButton, DialogTitle } from '../../../components/dialog';
import { StatusPill } from '../../../components/StatusPill';
import { StatusCodes, WorkflowScopes } from '../../../constants/workflows';
import { useStaffOrHasPermission } from '../../../hooks/useStaffOrHasPermission';
import { formatApiDate } from '../../../utils/date';
import { AssignmentCard } from '../Assignments/AssignmentCard';
import { ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY } from '../Assignments/queryKeys';

const STATUSES_TO_EXCLUDE: string[] = [
  StatusCodes.CANCELED,
  StatusCodes.DECLINED,
  StatusCodes.WITHDRAWN,
];
const RECENT_END_ON_WINDOW_DAYS = 30;

const formatDateParam = (date: Date): string => date.toISOString().slice(0, 10);

type AssignmentsBetaTabProps = {
  connection: ConnectedFacility;
};

export function AssignmentsTab({ connection }: AssignmentsBetaTabProps) {
  const location = useLocation();
  const providerId = connection.provider?.id;
  const facilityId = connection.facility?.id;

  const [selectedAssignmentId, setSelectedAssignmentId] = useState<string | null>(null);

  const endOnAfter = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - RECENT_END_ON_WINDOW_DAYS);
    return formatDateParam(d);
  }, []);

  const { data: assignments, isLoading: isLoadingAssignments } = useQuery({
    queryKey: [
      ORGANIZATION_ASSIGNMENTS_LIST_QUERY_KEY,
      'connection-drawer',
      providerId,
      facilityId,
      endOnAfter,
    ],
    queryFn: () =>
      AssignmentsService.assignmentsAssignmentList({
        provider: providerId,
        facility: facilityId,
        statusExclude: STATUSES_TO_EXCLUDE,
        endOnAfter,
      }),
    enabled: !!providerId && !!facilityId,
    select: (data) => data.results || [],
  });

  const assignmentList = (assignments as AssignmentList[]) || [];

  const selectedAssignment = useMemo(
    () => assignmentList.find((assignment) => assignment.id === selectedAssignmentId) || null,
    [assignmentList, selectedAssignmentId]
  );

  const connectionStatusCode = connection.current_status?.code;
  const canCreateAssignment =
    useStaffOrHasPermission('create__assignments__assignment') &&
    connection.provider?.source_type !== SourceTypeEnum.ORGANIZATION &&
    (connectionStatusCode === StatusCodes.ACTIVE ||
      connectionStatusCode === StatusCodes.AWAIT_PRVLG);

  const currentPath = window.location.pathname + window.location.search;
  const availabilityToken = connection.provider?.availability_token;
  const tokenParam = availabilityToken ? `&token=${availabilityToken}` : '';
  const createAssignmentUrl = providerId
    ? `/app/organization/assignments/create?provider=${providerId}&facility=${facilityId || ''}${tokenParam}&returnUrl=${encodeURIComponent(currentPath)}`
    : null;

  return (
    <div className="tw:space-y-1">
      <div className="tw:flex tw:items-center tw:justify-between">
        <div className="tw:flex tw:flex-col tw:gap-0">
          <div className="tw:text-lg tw:font-semibold tw:text-brand-black-2">Assignments</div>
          <div className="tw:text-xs tw:text-zinc-500">
            Future assignments and recently completed
          </div>
        </div>
        {canCreateAssignment && createAssignmentUrl && (
          <div className="tw:flex tw:justify-end">
            <Link to={createAssignmentUrl} className="tw:!no-underline">
              <Button small color="brand-green-outline">
                <i className="fas fa-plus tw:mr-1" />
                New Assignment
              </Button>
            </Link>
          </div>
        )}
      </div>
      <section>
        {isLoadingAssignments ? (
          <div className="tw:text-sm tw:text-zinc-500">Loading assignments...</div>
        ) : assignmentList.length > 0 ? (
          <div>
            {assignmentList.map((assignment) => {
              const startDate = assignment.start_on;
              const endDate = assignment.end_on;
              const shiftFacilities = (assignment as any).metadata?.shift_facilities || [];
              return (
                <button
                  type="button"
                  key={assignment.id}
                  className="tw:flex tw:flex-wrap tw:items-center tw:gap-x-3 tw:gap-y-1 tw:py-2 tw:border-b tw:border-zinc-100 last:tw:border-b-0 tw:cursor-pointer tw:w-full tw:text-left tw:bg-transparent tw:border-0 tw:p-0"
                  onClick={() => setSelectedAssignmentId(assignment.id)}
                >
                  {/* Column 1: Code + Dates */}
                  <div className="tw:flex tw:flex-col tw:gap-0 tw:min-w-0">
                    <span className="tw:text-sm tw:font-medium tw:text-brand-green-1 tw:hover:text-brand-green-0 tw:hover:underline ">
                      {assignment.code || 'Untitled assignment'}
                    </span>
                    {startDate && (
                      <span className="tw:text-[10px] tw:text-zinc-500 tw:font-mono tw:tracking-tight">
                        {formatApiDate(startDate, { dateFormat: 'MM/dd/yyyy' })}
                        {endDate && endDate !== startDate
                          ? ` - ${formatApiDate(endDate, { dateFormat: 'MM/dd/yyyy' })}`
                          : ''}
                      </span>
                    )}
                  </div>
                  {/* Column 2: Facility names */}
                  <div className="tw:flex tw:flex-col tw:gap-0 tw:min-w-0 tw:flex-1">
                    {shiftFacilities.length > 1 ? (
                      shiftFacilities.map((f: any) => (
                        <div
                          key={f.facility_id}
                          className="tw:text-xs tw:text-zinc-500 tw:truncate"
                          title={f.facility_name}
                        >
                          &bull; {f.facility_name}
                        </div>
                      ))
                    ) : (
                      <span className="tw:text-xs tw:text-zinc-500 tw:truncate">
                        {assignment.facility?.name}
                      </span>
                    )}
                  </div>
                  {/* Column 3: Status */}
                  <div className="tw:inline-flex tw:items-center tw:shrink-0">
                    <StatusPill
                      scope={WorkflowScopes.Assignment}
                      status={assignment.current_status || 'Unknown'}
                    />
                  </div>
                </button>
              );
            })}
          </div>
        ) : (
          <div className="tw:rounded-md tw:border tw:border-dashed tw:border-zinc-300 tw:dark:border-zinc-700 tw:p-6 tw:text-center tw:text-sm tw:text-zinc-500">
            No assignments matching criteria
          </div>
        )}
      </section>

      <Dialog open={!!selectedAssignment} onClose={() => setSelectedAssignmentId(null)} size="lg">
        <div className="tw:flex tw:justify-between tw:mb-2">
          <DialogTitle>Assignment</DialogTitle>
          <DialogCloseButton onClick={() => setSelectedAssignmentId(null)} />
        </div>
        {selectedAssignment && <AssignmentCard assignment={selectedAssignment} />}
        <div className="tw:flex tw:justify-end tw:mt-4">
          <Link
            to={`/app/organization/assignments/${selectedAssignment?.id}`}
            state={{ referrer: location.pathname + location.search }}
            className="tw:!no-underline"
          >
            <Button color="brandgreen0">View Full Details</Button>
          </Link>
        </div>
      </Dialog>
    </div>
  );
}
